import { getFunctions, httpsCallable } from 'firebase/functions';
import { BUCKET_FUNCTIONS_REGION, FUNCTIONS_REGION } from '../../functionsRegion';
import { formatFileSize } from '../../utils/fileFormatting';
import { GLOBAL_ALLOWED_FILE_TYPES } from '../../constants/fileTypes';

// Interface for show folder from backend
export interface ShowFolderRecord {
  id: string;
  showId: string;
  name: string;
  icon: string;
  /** @deprecated Folders hold mixed media - legacy field still returned by the backend for old mobile builds. */
  mediaType?: 'audio' | 'video' | 'document';
  /** @deprecated No longer enforced - uploads validate against the global whitelist. */
  allowedFileTypes?: string[];
  supportsFolders: boolean;
  sortOrder: number;
  description?: string;
  createdAt: Date;
  createdBy: string;
  isCustom: boolean;
  parentFolderId?: string;
  // Deprecated - kept for backwards compatibility
  parentCategory?: 'singing' | 'dancing' | 'acting';
}

// Interface for folders grouped by category
export interface FoldersByCategory {
  singing: ShowFolderRecord[];
  dancing: ShowFolderRecord[];
  acting: ShowFolderRecord[];
}

// Interface for folder counts
export interface FolderCounts {
  singing: number;
  dancing: number;
  acting: number;
  all: number;
}

// Helper function to determine file type from MIME type
export const getFileTypeFromMimeType = (mimeType: string) => {
  if (!mimeType) return 'document';
  
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.includes('pdf')) return 'document';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  
  return 'document';
};

// Re-export from shared utils for backwards compatibility
export { formatFileSize };

// Helper function to format duration in MM:SS format
export const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '--:--';
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Duration is cosmetic, so reading it must never be able to outlive the upload
// that is waiting on it.
const AUDIO_DURATION_TIMEOUT_MS = 15000;

// Helper function to extract audio duration from file.
//
// Never rejects and never hangs. Safari can fail a blob load without ever
// firing `error` on the media element - it surfaces only as "WebKitBlobResource
// error 1" in the console - which left this promise pending forever and wedged
// the caller. The timeout is the backstop for that.
export const getAudioDuration = (file: File): Promise<number | null> => {
  return new Promise((resolve) => {
    // Only process audio files
    if (!file.type.startsWith('audio/')) {
      resolve(null);
      return;
    }

    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);
    let settled = false;

    const finish = (duration: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('error', onError);
      // Detach the element before revoking. Safari keeps streaming the blob
      // past `loadedmetadata`, so revoking underneath it fails that read.
      audio.removeAttribute('src');
      audio.load();
      URL.revokeObjectURL(objectUrl);
      resolve(duration);
    };

    const onLoadedMetadata = () => {
      // Some containers report Infinity or NaN until fully buffered - a bad
      // number is worse than no number.
      const duration = audio.duration;
      finish(Number.isFinite(duration) && duration > 0 ? duration : null);
    };

    const onError = () => finish(null);

    const timer = setTimeout(() => finish(null), AUDIO_DURATION_TIMEOUT_MS);

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('error', onError);
    audio.preload = 'metadata';
    audio.src = objectUrl;
    audio.load();
  });
};

// Get all subcategories across all categories for "All" view
export const getAllSubcategories = (foldersByCategory: FoldersByCategory) => {
  const allSubs: {name: string, parentCategory: string, icon: string, folderId: string}[] = [];
  Object.entries(foldersByCategory).forEach(([category, folderList]) => {
    folderList.forEach((folder: ShowFolderRecord) => {
      allSubs.push({
        name: folder.name, 
        parentCategory: category, 
        icon: folder.icon,
        folderId: folder.id
      });
    });
  });
  return allSubs;
};

// Firebase functions instance - resolved lazily so importing this module does
// not require an initialised Firebase app (e.g. under Jest).
let functionsInstance: ReturnType<typeof getFunctions> | null = null;
const getFns = () => {
  if (!functionsInstance) functionsInstance = getFunctions(undefined, FUNCTIONS_REGION);
  return functionsInstance;
};

// Fetch the show's folders. Deliberately separate from the resource fetch:
// getShowFolders returns a handful of small documents and comes back long
// before getResources has finished reading every resource in the show, so
// splitting them lets the folder grid paint on the fast response instead of
// waiting for the slow one.
export const fetchShowFolders = async (showId: string) => {
  let foldersResult;
  try {
    const getShowFolders = httpsCallable(getFns(), 'getShowFolders');
    foldersResult = await getShowFolders({ showId });
  } catch (error) {
    console.error('Error fetching folders:', error);
    throw new Error(`Failed to load folders: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  const foldersData = foldersResult.data as any;
  let processedFolders: ShowFolderRecord[] = [];
  const foldersByCategory: FoldersByCategory = { singing: [], dancing: [], acting: [] };
  let folderCounts: FolderCounts = { singing: 0, dancing: 0, acting: 0, all: 0 };

  if (foldersData.folders) {
    // Convert Firestore timestamps to Date objects
    processedFolders = foldersData.folders.map((folder: any) => ({
      ...folder,
      createdAt: folder.createdAt?.toDate?.() || new Date(folder.createdAt)
    }));

    folderCounts = foldersData.counts || { all: processedFolders.length };
  }

  return { folders: processedFolders, foldersByCategory, folderCounts };
};

// Resolve a storage path to a download URL. Used for the handful of legacy
// resources written before addResource persisted downloadUrl; everything
// uploaded since carries its URL in the document.
export const resolveDownloadUrl = async (storagePath: string): Promise<string | null> => {
  try {
    const { ref, getDownloadURL } = await import('firebase/storage');
    const { storage } = await import('../../firebase');
    return await getDownloadURL(ref(storage, storagePath));
  } catch (error) {
    console.warn(`Could not resolve download URL for ${storagePath}:`, error);
    return null;
  }
};

// Fetch the show's resources, already grouped by folder path by the backend.
//
// This is a pure transform: no network calls per resource. Resolving missing
// download URLs here used to hold the entire tab in a skeleton behind N
// serialised Storage round trips; that now happens as a background backfill
// after first paint (see useResourcesData).
export const fetchShowResources = async (showId: string) => {
  let resourcesResult;
  try {
    const getResources = httpsCallable(getFns(), 'getResources');
    resourcesResult = await getResources({ showId });
  } catch (error) {
    console.error('Error fetching resources:', error);
    throw new Error(`Failed to load resources: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  const resourcesData = resourcesResult.data as any;
  const subcategoryFiles: {[key: string]: any[]} = {};

  if (resourcesData.resourcesByFolder) {
    Object.keys(resourcesData.resourcesByFolder).forEach(folderName => {
      subcategoryFiles[folderName] = resourcesData.resourcesByFolder[folderName].map(
        (resource: any) => {
          const downloadUrl = resource.downloadUrl || null;

          const formattedResource = {
            name: resource.label || 'Untitled',
            type: getFileTypeFromMimeType(resource.mimeType),
            size: (() => {
              // Use compressed size if compression is completed and we have compressed size
              if (resource.compressionStatus === 'completed' && resource.compressedSizeMB) {
                return formatFileSize(resource.compressedSizeMB * 1024 * 1024); // Convert MB to bytes
              }
              // Otherwise use original file size
              return formatFileSize(resource.fileSize || 0);
            })(),
            // The raw byte count beside the display string, so readers of this
            // cache (the Overview's recent uploads) get the same number the
            // server sends rather than parsing "2.4 MB" back.
            fileSize: resource.fileSize || 0,
            duration: resource.duration ? formatDuration(resource.duration) : undefined, // Add formatted duration
            // Compression status fields for videos
            compressionStatus: resource.compressionStatus || null,
            compressionStartedAt: resource.compressionStartedAt || null,
            compressionCompletedAt: resource.compressionCompletedAt || null,
            compressionQuality: resource.compressionQuality || null,
            originalSizeMB: resource.originalSizeMB || null,
            compressedSizeMB: resource.compressedSizeMB || null,
            compressionRatio: resource.compressionRatio || null,
            // Thumbnail status fields
            thumbnailStatus: resource.thumbnailStatus || null,
            thumbnailUrls: resource.thumbnailUrls || null,
            thumbnailGeneratedAt: resource.thumbnailGeneratedAt || null,
            uploadedAt: (() => {
              // Handle Firestore Timestamp object that's been serialized through Firebase Functions
              if (resource.createdAt && typeof resource.createdAt === 'object') {
                // Check if it's a serialized Firestore Timestamp with _seconds
                if ('_seconds' in resource.createdAt) {
                  return new Date(resource.createdAt._seconds * 1000);
                }
                // Check if it has toDate method (direct Firestore Timestamp)
                if ('toDate' in resource.createdAt && typeof resource.createdAt.toDate === 'function') {
                  return resource.createdAt.toDate();
                }
              }
              // Fallback to parsing as regular date or epoch (avoids falsely showing as "just uploaded")
              return resource.createdAt ? new Date(resource.createdAt) : new Date(0);
            })(),
            storagePath: resource.storagePath, // Include the actual storage path
            downloadUrl: downloadUrl, // Use the generated or existing download URL
            resourceId: resource.id,
            mimeType: resource.mimeType,
            // updatedAt for "Last Updated" column
            updatedAt: (() => {
              const raw = resource.updatedAt || resource.createdAt;
              if (raw && typeof raw === 'object') {
                if ('_seconds' in raw) return new Date(raw._seconds * 1000);
                if ('toDate' in raw && typeof raw.toDate === 'function') return raw.toDate();
              }
              return raw ? new Date(raw) : null;
            })(),
            // CRITICAL: Pass through subcategory field from raw resource
            subcategory: resource.subcategory,
            // Also pass through folderPath if it exists as backup
            folderPath: resource.folderPath
          };

          return formattedResource;
        }
      );
    });
  }

  // No pre-grouped resources: an empty map. Folder keys with no files are
  // handled by the callers, which read `subcategoryFiles[folder] ?? []`.
  return subcategoryFiles;
};

// downloadFolder runs beside the storage bucket rather than in
// FUNCTIONS_REGION - it streams every byte of the folder - so it needs its
// own functions instance.
let folderDownloadFns: ReturnType<typeof getFunctions> | null = null;

export interface FolderDownloadResult {
  url: string;
  fileName: string;
  fileCount: number;
  skippedCount: number;
  totalBytes: number;
}

// A multi-select within the folder being downloaded: sub-folders by their
// full path (each with everything beneath it) and files by resourceId.
export interface DownloadSelection {
  folders?: string[];
  resourceIds?: string[];
}

// Zip a folder and every sub-folder beneath it on the server and return a
// short-lived link to the archive. `folderPath` is the full slash-joined path
// ('Rehearsals/Scenes'), the same key resources are filed under, or '' for
// the whole show. With a `selection`, only those items inside `folderPath`
// are zipped, still laid out under its name. Big folders take a while - the
// server copies every file before the link exists.
export const handleDownloadFolder = async (
  showId: string,
  folderPath: string,
  selection?: DownloadSelection
): Promise<FolderDownloadResult> => {
  if (!folderDownloadFns) folderDownloadFns = getFunctions(undefined, BUCKET_FUNCTIONS_REGION);
  const downloadFolder = httpsCallable<{ showId: string; path: string } & DownloadSelection, FolderDownloadResult>(
    folderDownloadFns,
    'downloadFolder',
    // The server's own limit; the SDK default of 70s would give up first.
    { timeout: 1_800_000 }
  );
  const result = await downloadFolder({ showId, path: folderPath, ...selection });
  return result.data;
};

// Delete folder
export const handleDeleteCategory = async (showId: string, folderId: string) => {
  const deleteShowFolder = httpsCallable(getFns(), 'deleteShowFolder');
  const folderIdParts = folderId.split('_');
  const actualFolderId = folderIdParts.slice(1).join('_'); // Remove showId prefix
  
  await deleteShowFolder({
    showId,
    folderId: actualFolderId
  });
};

// Update file/resource
export const handleUpdateFile = async (resourceId: string, name: string) => {
  const updateResource = httpsCallable(getFns(), 'updateResource');
  
  await updateResource({
    resourceId,
    label: name
  });
};

// Move file/resource into another folder. `folderPath` is the destination's
// slash-joined path ('Rehearsals/Scenes/Act 1 Scene 4'), or '' for the root
// of Resources. The callable refuses a folder that no longer exists, so a
// stale dropdown cannot file something under a phantom path.
export const handleMoveFile = async (resourceId: string, folderPath: string) => {
  const moveResource = httpsCallable(getFns(), 'moveResource');
  await moveResource({ resourceId, folderPath });
};

// Delete file/resource
export const handleDeleteFile = async (resourceId: string) => {
  const deleteResource = httpsCallable(getFns(), 'deleteResource');
  
  await deleteResource({
    resourceId
  });
};

// Create file type requirements mapping from folders
// Display-only now: ResourcesTable uses this for its file-types column. New
// folders carry the full union; legacy folders keep their historic list.
export const createFileTypeRequirements = (folders: ShowFolderRecord[]): Record<string, string[]> => {
  const fileTypeRequirements: Record<string, string[]> = {};
  folders.forEach(folder => {
    fileTypeRequirements[folder.name] = folder.allowedFileTypes?.length
      ? folder.allowedFileTypes
      : GLOBAL_ALLOWED_FILE_TYPES;
  });
  return fileTypeRequirements;
};

// The table is the default view everywhere in Resources - it is denser, and it
// renders no thumbnails, so opening a folder costs no thumbnail generation
// round trips. Cards remain available from the view toggle. (This replaces the
// old content-sniffing getOptimalViewMode, which opened video/image folders in
// cards and made the slowest folders the ones that also fired N thumbnail
// callables on entry.)
export const DEFAULT_VIEW_MODE: 'cards' | 'table' = 'table';